import { useTitle } from '@vueuse/core'
import { computed, reactive, watch } from 'vue'
import { useRoute } from 'vue-router'

import { OWNERSHIP_LIFECYCLE_GRID } from '@/constants'
import { getModelIconClasses } from '@/utils'

import { useBreakpoints } from './layouts/useBreakpoints'
import { useAttachments } from './useAttachments'
import { useCopyNotify } from './useCopyNotify'
import { useDeletableRecord } from './useDeletableRecord'
import { useEditableRecord } from './useEditableRecord'
import { formatTabTitle } from './usePageShell'
import { useRecord } from './useRecord'
import { useRecordMeta } from './useRecordMeta'

/**
 * Combined composable for detail views.
 * Wires together record, editable, meta, deletable, attachments, and breakpoints.
 */
export function useDetailView({
  appName,
  modelName,
  modelType,
  id,
  skipFieldNames = [],
  queryParameters = {},
  deleteQueryParams = {},
}) {
  const route = useRoute()
  // --- Record ---
  const recordResult = useRecord(
    { appName, modelName, id },
    { queryParameters },
  )
  const {
    isLoadingRecord,
    fetchRecordData,
    loadRecord,
    errorData,
    record,
    id: recordId,
    name: recordName,
    recordStatusKey,
    recordStatus,
    canView,
    canEdit,
    canDelete,
    isRecordLoaded,
    isDeleted,
    removeField,
  } = recordResult

  // --- Meta ---
  const {
    meta,
    isMetaReady,
    modelAttributes,
    viewAttributes,
    annotationTypes,
    defaultRecord,
    visibleFields,
    listedFields,
    orderedFields,
    customProperties,
    isAnnotatable,
    isAttachmentable,
    isOrderable,
    isNoteable,
    isShippable,
    addFieldMetaPatch,
  } = useRecordMeta({ appName, modelName, modelType, skipFieldNames })

  // --- Editable ---
  const {
    edits,
    edit,
    hasEdits,
    hasValidationErrors,
    isEditMode,
    isEditing,
    toggleEditMode,
    enterEdit,
    exitEdit,
    discardEdits,
    discardAndExit,
    nextRoute,
    canSaveEdits,
    saveEdits,
    saveAndExit,
    onSuccessfulSave,
    isSaving,
    editedRecord,
    resetField,
    resetFields,
    v$,
    permissions,
  } = useEditableRecord(record, undefined, defaultRecord, {
    appName,
    modelName,
  })

  // --- Deletable ---
  const { isDeleting, deleteRecord, isRestoring, restoreRecord } =
    useDeletableRecord(record, deleteQueryParams)

  // --- Attachments ---
  // Always construct the composable (it holds shared singleton state consumed
  // by the editor / image-embed too); gate its fetch on the model's
  // attachmentable capability so non-attachmentable models never hit the API.
  const attachmentsResult = useAttachments(id, {
    appName,
    modelName,
    enabled: isAttachmentable,
  })

  // --- Breakpoints ---
  const breakpointsResult = useBreakpoints()

  // --- Field helpers ---

  // Dataset-specific: parent_samples is readonly when managed and raw
  if (modelName === 'datasets') {
    addFieldMetaPatch(
      computed(() => {
        const isManaged = record.value.is_managed?.value
        const isRaw = record.value.is_raw?.value
        if (isManaged && isRaw) {
          return { parent_samples: { readonly: true } }
        }
        return {}
      }),
    )
  }

  // --- Ownership/lifecycle grid (FieldGrid-compatible, centralized) ---
  // deleted_by/deleted_date are reactively hidden when the record is not deleted
  const DELETED_ONLY_FIELDS = ['deleted_by', 'deleted_date']
  const ownershipLifecycleGrid = computed(() => {
    const visible = new Set(visibleFields.value.map((f) => f.name))
    const isHiddenDeleteField = (name) =>
      !isDeleted.value && DELETED_ONLY_FIELDS.includes(name)

    // Invisible fields become null placeholders (not dropped) so the row keeps
    // its column skeleton — a lone "modified" still aligns with the pairs
    // around it instead of stretching full-width.
    return OWNERSHIP_LIFECYCLE_GRID.map((row) =>
      row.map((n) =>
        n && visible.has(n) && !isHiddenDeleteField(n) ? n : null,
      ),
    ).filter((row) => row.some(Boolean))
  })

  // --- Copy link ---
  const url = computed(() => {
    const base = window.location.origin
    return `${base}${route.path}`
  })
  const { copyWithNotify } = useCopyNotify()
  function handleCopyLink() {
    copyWithNotify(url.value, 'URL')
  }

  // --- Page title ---
  const title = useTitle()
  watch(
    recordName,
    (name) => {
      title.value = formatTabTitle(name)
    },
    { immediate: true },
  )

  const modelIconClasses = computed(() => {
    const classes = getModelIconClasses(appName, modelName)
    return classes.includes('fa-question-circle') ? [] : classes
  })

  return reactive({
    // route params
    appName,
    modelName,
    modelType,

    // record
    isLoadingRecord,
    fetchRecordData,
    loadRecord,
    errorData,
    record,
    recordId,
    recordName,
    recordStatusKey,
    recordStatus,
    canView,
    canEdit,
    canDelete,
    isRecordLoaded,
    isDeleted,
    removeField,

    // meta
    meta,
    isMetaReady,
    modelAttributes,
    viewAttributes,
    annotationTypes,
    defaultRecord,
    visibleFields,
    listedFields,
    orderedFields,
    ownershipLifecycleGrid,
    customProperties,
    isAnnotatable,
    isAttachmentable,
    isOrderable,
    isNoteable,
    isShippable,

    // editable
    edits,
    edit,
    hasEdits,
    hasValidationErrors,
    isEditMode,
    isEditing,
    toggleEditMode,
    enterEdit,
    exitEdit,
    discardEdits,
    discardAndExit,
    nextRoute,
    canSaveEdits,
    saveEdits,
    saveAndExit,
    onSuccessfulSave,
    isSaving,
    editedRecord,
    resetField,
    resetFields,
    v$,
    permissions,

    // deletable
    isDeleting,
    deleteRecord,
    isRestoring,
    restoreRecord,

    // attachments
    attachments: attachmentsResult.attachments,
    rawAttachments: attachmentsResult.rawAttachments,
    loadAttachments: attachmentsResult.loadAttachments,
    unloadAttachments: attachmentsResult.unloadAttachments,
    isLoadingAttachments: attachmentsResult.isLoading,
    uploadAttachment: attachmentsResult.uploadAttachment,
    handleAttachmentsUpload: attachmentsResult.handleAttachmentsUpload,
    isUploadingAttachment: attachmentsResult.isUploading,
    deleteAttachment: attachmentsResult.deleteAttachment,
    deleteAttachments: attachmentsResult.deleteAttachments,
    deletingAttachmentsIds: attachmentsResult.deletingAttachmentsIds,
    downloadAttachment: attachmentsResult.downloadAttachment,
    getAttachmentBlob: attachmentsResult.getAttachmentBlob,
    imagesReadyForEmbed: attachmentsResult.imagesReadyForEmbed,
    uploadImageCopy: attachmentsResult.uploadImageCopy,
    deleteSuccesses: attachmentsResult.deleteSuccesses,
    doAndReload: attachmentsResult.doAndReload,
    getAttachmentUrl: attachmentsResult.getAttachmentUrl,

    // breakpoints
    ...breakpointsResult,

    // field helpers
    addFieldMetaPatch,

    // utilities
    url,
    handleCopyLink,
    modelIconClasses,
  })
}
